<template>
  <div
    v-for="alert in alerts"
    :key="alert.key"
    :class="[
      'alert d-flex align-items-center gap-2 mb-3',
      alert.holdsShipment ? 'alert-danger' : 'alert-warning',
    ]"
    role="alert"
    data-testid="price-conflict-warning"
  >
    <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
    <span>{{ alert.text }}</span>
  </div>
</template>

<script setup lang="ts">
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import { usePriceConflictText } from "@/forwards2/deals/usePriceConflictText";
import type { DeliveryResponse } from "@/forwards2/forwardsTypes";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * One alert per company and delivery whose mandates disagree on its price, at the top of the deal
 * page. One that holds the shipment says the delivery cannot ship until it is resolved; a foreseen
 * one — the company is a member of a mandate its escrow has not come through — says it will once
 * allocation brings that escrow. A shipped delivery carries no conflicts.
 */
const props = defineProps<{
  deliveries: DeliveryResponse[];
}>();

const { t } = useI18n();
const deliveryWindowText = useDeliveryWindowText();
const priceConflictText = usePriceConflictText();

const alerts = computed(() =>
  props.deliveries.flatMap((delivery) =>
    delivery.price_conflicts.map((conflict) => ({
      key: `${delivery.id}:${conflict.company_id}`,
      holdsShipment: conflict.holds_shipment,
      text: t(
        conflict.holds_shipment
          ? "forwards.deals.price_conflict.warning"
          : "forwards.deals.price_conflict.foreseen_warning",
        {
          company: conflict.company_name,
          mandates: priceConflictText(conflict),
          window: deliveryWindowText(delivery),
        }
      ),
    }))
  )
);
</script>
